export interface Translate {
  lang: string;
  t: (key: string, vars?: Record<string, unknown>) => string;
}

export type TranslationDictionary = Record<string, string>;
export type TranslationMap = Record<string, TranslationDictionary>;

const RTL_LANGS = new Set(["ar", "he"]);

export function normalizeLocale(locale?: string): string | undefined {
  if (!locale) {
    return undefined;
  }

  const sanitized = locale.trim().replace(/_/g, "-");
  if (!sanitized) {
    return undefined;
  }

  const parts = sanitized.split("-");
  return parts
    .map((part, index) => (index === 0 ? part.toLowerCase() : part.toUpperCase()))
    .join("-");
}

function findLocaleMatch(available: string[], candidate?: string): string | undefined {
  if (!candidate) {
    return undefined;
  }

  const lower = candidate.toLowerCase();
  return available.find((locale) => locale.toLowerCase() === lower);
}

export interface ResolveLocaleOptions {
  locale?: string;
  defaultLocale: string;
  availableLocales: string[];
}

export function resolveLocale(options: ResolveLocaleOptions): string {
  const { locale, defaultLocale, availableLocales } = options;
  const normalized = normalizeLocale(locale);

  const exact = findLocaleMatch(availableLocales, normalized);
  if (exact) {
    return exact;
  }

  const base = normalized?.split("-")[0];
  if (base) {
    const baseMatch = findLocaleMatch(availableLocales, base);
    if (baseMatch) {
      return baseMatch;
    }

    const regionalFallback = availableLocales.find(
      (candidate) => candidate.split("-")[0].toLowerCase() === base.toLowerCase()
    );
    if (regionalFallback) {
      return regionalFallback;
    }
  }

  return defaultLocale;
}

export function interpolate(input: string, vars?: Record<string, unknown>): string {
  if (!vars) {
    return input;
  }

  return Object.entries(vars).reduce(
    (result, [key, value]) => result.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), String(value)),
    input
  );
}

export function isRightToLeft(locale: string): boolean {
  const normalized = normalizeLocale(locale);
  const base = normalized?.split("-")[0] ?? locale;
  return RTL_LANGS.has(base.toLowerCase());
}

export interface CreateTranslateFactoryOptions {
  translations: TranslationMap;
  defaultLocale?: string;
}

export type TranslateFactory = (locale?: string) => Translate;

export function createTranslateFactory(options: CreateTranslateFactoryOptions): TranslateFactory {
  const { translations } = options;
  const availableLocales = Object.keys(translations);
  const fallback =
    options.defaultLocale && translations[options.defaultLocale]
      ? options.defaultLocale
      : availableLocales[0] ?? options.defaultLocale ?? "en";

  return (locale?: string) => {
    const code = resolveLocale({
      locale,
      defaultLocale: fallback,
      availableLocales,
    });

    const dictionary = translations[code] ?? translations[fallback] ?? {};
    const fallbackDictionary = code === fallback ? undefined : translations[fallback];

    return {
      lang: code,
      t: (key: string, vars?: Record<string, unknown>) => {
        const value = dictionary[key] ?? fallbackDictionary?.[key] ?? key;
        return interpolate(value, vars);
      },
    };
  };
}

export interface CreateTranslateOptions extends CreateTranslateFactoryOptions {
  locale?: string;
}

export function createTranslate(options: CreateTranslateOptions): Translate {
  const factory = createTranslateFactory({
    translations: options.translations,
    defaultLocale: options.defaultLocale,
  });
  return factory(options.locale);
}
